import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrendingUp } from 'lucide-react';

/**
 * Internal-link booster block for the homepage.
 * Uses keyword-rich anchor text to push pages currently ranking on
 * Google pages 1–2 (presoterapija, kiropraktika, plantarni fascitis,
 * bol u leđima i vratu, shockwave, kineziterapija) toward the top 10.
 */
const PopularSearchLinks = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const heading = lang === 'en'
    ? 'Most searched on PHYSIOWRLD'
    : lang === 'ru'
    ? 'Чаще всего ищут на PHYSIOWRLD'
    : 'Najtraženije na PHYSIOWRLD-u';

  const subtitle = lang === 'en'
    ? 'Quick access to our most requested treatments and conditions'
    : lang === 'ru'
    ? 'Быстрый доступ к самым востребованным процедурам и состояниям'
    : 'Brz pristup najtraženijim tretmanima i dijagnozama';

  const links = lang === 'en' ? [
    { to: '/services/pressotherapy', label: 'Pressotherapy in Belgrade' },
    { to: '/kiropraktika-novi-beograd', label: 'Chiropractor in Belgrade' },
    { to: '/services/shockwave', label: 'Shockwave therapy in Belgrade' },
    { to: '/services/kinesitherapy', label: 'Kinesitherapy in Belgrade' },
    { to: '/petni-trn', label: 'Plantar fasciitis treatment' },
    { to: '/bol-u-ledjima-tretman-beograd', label: 'Back pain treatment' },
    { to: '/bol-u-vratu-tretman-beograd', label: 'Neck pain & headache treatment' },
    { to: '/limfna-drenaza-novi-beograd', label: 'Lymphatic drainage New Belgrade' },
    { to: '/tretman-celulita-novi-beograd', label: 'Cellulite & fat removal Shockwave' },
    { to: '/blog/petni-trn-uzroci-tretman', label: 'How to treat heel spur' },
    { to: '/blog/fizikalna-terapija-posle-operacije-kuka', label: 'Exercises after hip surgery' },
    { to: '/fizikalna-terapija-beograd', label: 'Physical therapy Belgrade — complete guide' },
  ] : lang === 'ru' ? [
    { to: '/services/pressotherapy', label: 'Прессотерапия в Белграде' },
    { to: '/kiropraktika-novi-beograd', label: 'Хиропрактик в Белграде' },
    { to: '/services/shockwave', label: 'Ударно-волновая терапия Белград' },
    { to: '/services/kinesitherapy', label: 'Кинезитерапия в Белграде' },
    { to: '/petni-trn', label: 'Лечение пяточной шпоры' },
    { to: '/bol-u-ledjima-tretman-beograd', label: 'Лечение боли в спине' },
    { to: '/bol-u-vratu-tretman-beograd', label: 'Лечение боли в шее и головы' },
    { to: '/limfna-drenaza-novi-beograd', label: 'Лимфодренаж Новый Белград' },
    { to: '/tretman-celulita-novi-beograd', label: 'Удаление целлюлита Shockwave' },
    { to: '/blog/petni-trn-uzroci-tretman', label: 'Как лечить пяточную шпору' },
    { to: '/blog/fizikalna-terapija-posle-operacije-kuka', label: 'Упражнения после операции тазобедренного сустава' },
    { to: '/fizikalna-terapija-beograd', label: 'Физиотерапия Белград — полный гид' },
  ] : [
    { to: '/services/pressotherapy', label: 'Presoterapija Beograd' },
    { to: '/kiropraktika-novi-beograd', label: 'Kiropraktika Beograd — kiropraktičar' },
    { to: '/services/shockwave', label: 'Shockwave terapija Beograd — cene' },
    { to: '/services/kinesitherapy', label: 'Kineziterapija Beograd' },
    { to: '/petni-trn', label: 'Petni trn i plantarni fascitis — lečenje' },
    { to: '/bol-u-ledjima-tretman-beograd', label: 'Bol u leđima — tretman' },
    { to: '/bol-u-vratu-tretman-beograd', label: 'Bol u vratu i glavobolje — terapija' },
    { to: '/limfna-drenaza-novi-beograd', label: 'Limfna drenaža Novi Beograd' },
    { to: '/tretman-celulita-novi-beograd', label: 'Skidanje masti i celulita — Shockwave' },
    { to: '/blog/petni-trn-uzroci-tretman', label: 'Kako razbiti petni trn — vodič' },
    { to: '/blog/fizikalna-terapija-posle-operacije-kuka', label: 'Vežbe za oporavak posle operacije kuka' },
    { to: '/fizikalna-terapija-beograd', label: 'Fizikalna terapija Beograd — kompletan vodič' },
  ];

  return (
    <section className="py-16 bg-muted/30" aria-labelledby="popular-searches-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-4">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm font-semibold">
              {lang === 'en' ? 'Quick links' : lang === 'ru' ? 'Быстрые ссылки' : 'Brzi linkovi'}
            </span>
          </div>
          <h2 id="popular-searches-heading" className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            {heading}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">{subtitle}</p>
        </div>

        <ul className="flex flex-wrap justify-center gap-3 max-w-5xl mx-auto">
          {links.map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                className="inline-block px-4 py-2 rounded-full bg-card border border-border text-sm font-medium text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default PopularSearchLinks;
